import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/form';
import {
  optionalAddress,
  requiredAddress,
  requiredPastDate,
  requiredState,
  requiredString,
  requiredZipCode,
} from '@/util/validators';
import { useTranslation } from '@/contexts/i18n-context';
import { StateSelector } from '@/components/form-controls/state-selector';
import clsx from 'clsx';
import {
  GetInsuranceInfoForEncounterResponseInsuranceEncounter,
  GetInsurancePlansResponse,
  GetPersonalInfoResponse,
  InsuranceRelationResponse,
  SaveInsuranceErrorType,
  SaveInsuranceResponse,
} from '@/api/api.schemas';
import { useMediaQuery } from '@/hooks/use-media-query';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { TextInput } from '@/components/inputs/text-input';
import styles from './insurance-information-page.module.css';
import { Checkbox } from '@/components/inputs/checkbox';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { Button } from '@/components/button';
import { useEncounter } from '@/contexts/encounter-context';
import { useGetEncountersInsurance } from '@/api/get-insurance-info-for-encounter';
import { useGetInsurancesPlans } from '@/api/get-insurance-plans';
import { Select } from '@/components/inputs/select';
import { useCreateEncountersInsurance } from '@/api/save-insurance';
import { Navigate } from '@/util/react-router-dom';
import { useBranding } from '@/outlets/branding-outlet';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { useState } from 'react';
import { Modal, ModalBody, ModalButtonContainer } from '@/components/modal/modal';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { createEditUrl } from '@/util/helpers';
import { PostSubmitPolicy, usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { useSaveProgressProps } from '@/hooks/use-save-progress-props';
import { CancelSvg } from '@/components/svgs/cancel';
import { Loader } from '@/components/loaders/loader';
import { formatPrice } from '@/util/formats';
import { useLoggedUser } from '@/contexts/current-user-context';
import { useGetPatient } from '@/api/get-personal-info';
import { useGetInsuranceRelations } from '@/api/get-insurance-relations';
import { Banner } from '@/components/banner';

const translationBase = 'visit.insuranceInformation';

const schemaWithoutAddress = z.object({
  subscriberAddressSameAsPatient: z.literal(true),
  insuredRelationshipId: requiredString(),
  insurancePlanId: requiredString(),
  insuranceId: requiredString(),
  subscriberFirstName: requiredString(),
  subscriberLastName: requiredString(),
  subscriberBirthday: requiredPastDate(),
});

const schemaWithAddress = schemaWithoutAddress.extend({
  subscriberAddressSameAsPatient: z.literal(false),
  address: requiredAddress(),
  address2: optionalAddress(),
  city: requiredString(),
  state: requiredState(),
  zipCode: requiredZipCode(),
});

const schema = z.discriminatedUnion('subscriberAddressSameAsPatient', [
  schemaWithAddress,
  schemaWithoutAddress,
]);

type FormValues = z.infer<typeof schema>;

interface InsuranceInformationFormProps {
  insurance?: GetInsuranceInfoForEncounterResponseInsuranceEncounter | null;
  plans: GetInsurancePlansResponse;
  patient: GetPersonalInfoResponse;
  relations: InsuranceRelationResponse[];
}

function shouldUsePatientAsSubscriber(
  relations: InsuranceRelationResponse[],
  relationId: string | null | undefined,
) {
  return relations.find((relation) => relation.id === relationId)?.usePatientAsSubscriber ?? false;
}

export function InsuranceInformationPage() {
  const { user } = useLoggedUser();
  const { encounter } = useEncounter();
  const { networkInsuranceCarrierId, hasInsurance } = useBranding();
  const { data: insurance, isFetching } = useGetEncountersInsurance(encounter.id);
  const { data: patient, isFetching: isPatientFetching } = useGetPatient(user.id);
  const { data: relations, isFetching: isFetchingRelations } = useGetInsuranceRelations();
  const { data: plans, isFetching: isFetchingPlans } = useGetInsurancesPlans(
    networkInsuranceCarrierId!,
    {
      query: {
        enabled: hasInsurance,
      },
    },
  );

  if (!hasInsurance) {
    return <Navigate to="/" />;
  }

  if (isFetching || isFetchingPlans || isPatientFetching || isFetchingRelations) {
    return <Loader type="large" />;
  }

  if (!insurance || !plans || !patient || !relations) {
    return <Navigate to="/system-error" />;
  }

  return (
    <InsuranceInformationForm
      insurance={insurance.insuranceEncounter}
      plans={plans}
      patient={patient}
      relations={relations}
    />
  );
}

function InsuranceInformationForm({
  insurance,
  plans,
  patient,
  relations,
}: InsuranceInformationFormProps) {
  const { t } = useTranslation();
  const { encounter, invalidateEncounter } = useEncounter();
  const { networkInsuranceCarrierId, usePatientIdNumberLabel } = useBranding();
  const [errorModalState, setErrorModalState] = useState<
    { type: 'submit' | 'continue-later'; basePrice?: number | null } | undefined
  >();

  const { navigatePostSubmit } = usePostSubmitNavigation();
  const [insuranceError, setInsuranceError] = useState<string>();

  const { mutate: saveInsurance, isPending } = useCreateEncountersInsurance();

  const usePatientAsSubscriber = shouldUsePatientAsSubscriber(
    relations,
    insurance?.insuranceRelationId,
  );
  const defaultValues = insurance
    ? {
        insuredRelationshipId: insurance.insuranceRelationId ?? '',
        insurancePlanId: insurance.insurancePlanId ?? '',
        insuranceId: insurance.insuranceId ?? '',
        subscriberFirstName: usePatientAsSubscriber
          ? patient.firstName
          : (insurance.insuredFirstName ?? ''),
        subscriberLastName: usePatientAsSubscriber
          ? patient.lastName
          : (insurance.insuredLastName ?? ''),
        subscriberBirthday: usePatientAsSubscriber
          ? (patient.dateOfBirth ?? '')
          : (insurance.insuredBirthday ?? ''),
        subscriberAddressSameAsPatient: insurance.insuredSameAddress ?? true,
        address: insurance.insuredSameAddress ? '' : (insurance.insuredPrimaryAddr1 ?? ''),
        address2: insurance.insuredSameAddress ? '' : (insurance.insuredPrimaryAddr2 ?? ''),
        city: insurance.insuredSameAddress ? '' : (insurance.insuredPrimaryCity ?? ''),
        state: insurance.insuredSameAddress ? '' : (insurance.insuredPrimaryState ?? ''),
        zipCode: insurance.insuredSameAddress ? '' : (insurance.insuredPrimaryZip ?? ''),
      }
    : { subscriberAddressSameAsPatient: true };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    criteriaMode: 'firstError',
    defaultValues,
  });

  const toCoveragePage = (res: SaveInsuranceResponse, editMode: string | null) => ({
    path: createEditUrl(`/visit/${encounter.id}/insurance-coverage`, editMode),
    state: {
      insuranceActive: res.isInsuranceActive ?? false,
      visitPrice: res.maximumPrice,
      estimatedCost: res.calculatedPrice,
      insuranceCoverage: res.insuranceCoverage,
      hasVisitWithinSevenDays: res.hasVisitsWithinSevenDays,
      isGovernmentProgram: res.isGovernmentProgram,
      requiresPcpSpecialistReferral: res.requiresPcpSpecialistReferral,
    },
    replace: true,
  });

  const onSubmit = async (formData: FormValues, isIncomplete: boolean) => {
    setInsuranceError(undefined);

    saveInsurance(
      {
        encounterId: encounter.id,
        data: {
          ...formData,
          insuranceCarrierId: networkInsuranceCarrierId!,
        },
      },
      {
        onSuccess: async (res) => {
          await invalidateEncounter();
          const policy: PostSubmitPolicy = {
            resolveComplete: ({ editMode }) => toCoveragePage(res, editMode),
          };

          if (!isIncomplete) {
            // If isIncomplete is true, that means that the user has either clicked "To Review" or "Save and continue later".
            // If the user has clicked To Review, we want to redirect them to the review page,
            // otherwise (the patient has clicked "Save changes") we want to redirect them to the coverage page.
            policy.resolveEdit = ({ editMode }) => toCoveragePage(res, editMode);
          }

          navigatePostSubmit({
            isIncomplete,
            policy,
          });
        },
        onError: async (error) => {
          await invalidateEncounter();

          if (error.type === SaveInsuranceErrorType.SubscriberDoesNotMatchPatient) {
            setInsuranceError(t(`${translationBase}.subscriberDoesNotMatchPatientError`));
            return;
          }

          setErrorModalState({
            type: isIncomplete ? 'continue-later' : 'submit',
            basePrice: error.basePrice,
          });
        },
      },
    );
  };

  const continueWithoutInsurance = () => {
    const isIncomplete = errorModalState?.type === 'continue-later';
    navigatePostSubmit({ isIncomplete });
  };

  const isMobile = useMediaQuery('--screen-md');
  const useSameAddress = form.watch('subscriberAddressSameAsPatient');
  const saveProgressProps = useSaveProgressProps(form, (data) => onSubmit(data, true));
  const relationShip = form.watch('insuredRelationshipId');
  const isPatientUsedAsSubscriber = shouldUsePatientAsSubscriber(relations, relationShip);

  return (
    <PageContainer>
      {!isMobile && <VisitTitleButtons saveProgress={saveProgressProps} />}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons />}
        <VisitNavigation />
        <h1 className={clsx('h3', styles.pageTitle)}>{t(`${translationBase}.pageTitle`)}</h1>
        <p>{t(`${translationBase}.pageDescription`)}</p>
        {isMobile && <SaveProgressButton {...saveProgressProps} />}
      </GradientFormTitle>

      <Form form={form} onSubmit={(data) => onSubmit(data, false)}>
        <Section>
          <div className={styles.formInputs}>
            {insuranceError && (
              <Banner type="error" focusOnMount className={styles.fullRow}>
                {insuranceError}
              </Banner>
            )}

            <Select
              name="insuredRelationshipId"
              className={styles.fullRow}
              label={t(`${translationBase}.relationShipSelector.label`)}
              placeholder={t(`${translationBase}.relationShipSelector.placeholder`)}
              key={relations.map((relation) => relation.id).join(',')}
              options={relations.map((relation) => ({
                value: relation.id,
                label: relation.title,
              }))}
              onChange={(option) => {
                if (shouldUsePatientAsSubscriber(relations, option.value)) {
                  form.setValue('subscriberFirstName', patient.firstName);
                  form.setValue('subscriberLastName', patient.lastName);
                  form.setValue('subscriberBirthday', patient.dateOfBirth ?? '');
                }
              }}
            />
            <Select
              name="insurancePlanId"
              key={plans.plans.join(',')}
              label={t(`${translationBase}.insurance.plan.label`)}
              placeholder={t(`${translationBase}.insurance.plan.placeholder`)}
              options={plans.plans.map((plan) => ({
                value: plan.insurancePlanId.toString(),
                label: plan.description,
              }))}
            />
            <TextInput
              name="insuranceId"
              label={t(
                `${translationBase}.insurance.${usePatientIdNumberLabel ? 'insurancePatientId' : 'insuranceId'}.label`,
              )}
              placeholder={t(
                `${translationBase}.insurance.${usePatientIdNumberLabel ? 'insurancePatientId' : 'insuranceId'}.placeholder`,
              )}
            />
            <TextInput
              name="subscriberFirstName"
              label={t(`${translationBase}.subscriberName.firstName.label`)}
              placeholder={t(`${translationBase}.subscriberName.firstName.placeholder`)}
              description={t(`${translationBase}.subscriberName.firstName.helpText`)}
              disabled={isPatientUsedAsSubscriber}
            />
            <TextInput
              name="subscriberLastName"
              label={t(`${translationBase}.subscriberName.lastName.label`)}
              placeholder={t(`${translationBase}.subscriberName.lastName.placeholder`)}
              description={t(`${translationBase}.subscriberName.lastName.helpText`)}
              disabled={isPatientUsedAsSubscriber}
            />
            <TextInput
              name="subscriberBirthday"
              label={t(`${translationBase}.dateOfBirth.label`)}
              type="date"
              withIconsIndicators={false}
              disabled={isPatientUsedAsSubscriber}
            />

            <Checkbox
              className={styles.fullRow}
              label={t(`${translationBase}.sameAddress.label`)}
              name="subscriberAddressSameAsPatient"
            />

            {!useSameAddress && (
              <>
                <TextInput
                  name="address"
                  className={styles.fullRow}
                  label={t(`${translationBase}.address.address1.label`)}
                  placeholder={t(`${translationBase}.address.address1.placeholder`)}
                  description={t(`${translationBase}.address.address1.description`)}
                />
                <TextInput
                  name="address2"
                  className={styles.fullRow}
                  label={t(`${translationBase}.address.address2.label`)}
                  placeholder={t(`${translationBase}.address.address2.placeholder`)}
                  description={t(`${translationBase}.address.address2.description`)}
                />
                <TextInput
                  name="city"
                  label={t(`${translationBase}.address.city.label`)}
                  placeholder={t(`${translationBase}.address.city.placeholder`)}
                />
                <StateSelector
                  name="state"
                  placeholder={t(`${translationBase}.address.state.placeholder`)}
                  label={t(`${translationBase}.address.state.label`)}
                />
                <TextInput
                  name="zipCode"
                  label={t(`${translationBase}.address.zip.label`)}
                  placeholder={t(`${translationBase}.address.zip.placeholder`)}
                />
              </>
            )}
          </div>
        </Section>

        <Modal isOpen={!!errorModalState} onClose={() => setErrorModalState(undefined)}>
          <CancelSvg className={styles.icon} />
          <h2 className="h4">{t(`${translationBase}.errorModal.title`)}</h2>
          <ModalBody>
            <>
              {t(`${translationBase}.errorModal.text`)}

              {errorModalState?.basePrice != null && (
                <div className={styles.visitPrice}>
                  <p className="semibold">{t(`${translationBase}.errorModal.visitPrice`)}</p>
                  <span className={clsx('body-1', 'bold')}>
                    {formatPrice(errorModalState.basePrice)}
                  </span>
                </div>
              )}
            </>
          </ModalBody>
          <ModalButtonContainer>
            <Button variant="outlined" onClick={continueWithoutInsurance}>
              {t(`${translationBase}.errorModal.skipInsurance`)}
            </Button>
            <Button onClick={() => setErrorModalState(undefined)}>
              {t(`${translationBase}.errorModal.editInsurance`)}
            </Button>
          </ModalButtonContainer>
        </Modal>

        <VisitFormButtonRow
          nextStep={t(`${translationBase}.nextStep`)}
          buttonTextKey="common.submit"
          type="submit"
          rightIcon="arrow_forward"
          loading={isPending}
        />
      </Form>
    </PageContainer>
  );
}
